import { AxiosProgressEvent } from "axios";
import { APIPromise, queryClient } from "../services/HttpService";
import { FileQueryKey } from "./api-hooks/file-hooks";
import { ApiDefinition } from "./ApiDefinition";
import { InstantString } from "./model/general/aliases";

export interface FileUploadDTO {
  id: string;
  fileName: string;
  downloadUrlPart: string;
  isPublic: boolean;
  description: string;
  tags: string[];
  createdBy: number;
  createdAt: InstantString;
  editedAt: InstantString;
}

export interface FileUploadRequest {
  file?: File;
  id?: string;
  fileName?: string;
  desc?: string;
  tags?: string[];
  progressCallback?: (percent: number) => void;
}

export interface PublicFileUploadRequest extends FileUploadRequest {
  isPublic: boolean;
}

interface FileUploadWithUrlRequest extends FileUploadRequest {
  urlPath: string;
}

class FilesApiDefinition extends ApiDefinition {
  constructor() {
    super("/files");
  }

  async getPublicFiles(): APIPromise<FileUploadDTO[]> {
    return this.get("public");
  }

  async getCompanyFiles(): APIPromise<FileUploadDTO[]> {
    return this.get("company");
  }

  async uploadFileGeneral(req: PublicFileUploadRequest): APIPromise<FileUploadDTO> {
    return req.isPublic ? this.uploadPublicFile(req) : this.uploadCompanyFile(req);
  }

  async uploadCompanyFile(req: FileUploadRequest): APIPromise<FileUploadDTO> {
    const ret = await this.uploadFileCommon({
      urlPath: "upload",
      id: req.id,
      file: req.file,
      fileName: req.fileName,
      desc: req.desc,
      tags: req.tags,
      progressCallback: req.progressCallback,
    });

    if (ret.data) {
      queryClient.setQueryData([FileQueryKey.COMPANY_FILES], (oldData: FileUploadDTO[]) => {
        return oldData ? [ret.data!!, ...oldData] : [ret.data!!];
      });
    }

    return ret;
  }

  async uploadPublicFile(req: FileUploadRequest): APIPromise<FileUploadDTO> {
    const ret = await this.uploadFileCommon({
      urlPath: "public/upload",
      file: req.file,
      fileName: req.fileName,
      desc: req.desc,
      tags: req.tags,
      progressCallback: req.progressCallback,
    });

    if (ret.data) {
      queryClient.setQueryData([FileQueryKey.PUBLIC_FILES], (oldData: FileUploadDTO[]) => {
        return oldData ? [ret.data!!, ...oldData] : [ret.data!!];
      });
    }

    return ret;
  }

  async deleteCompanyFile(downloadUrlPart: string): APIPromise<FileUploadDTO> {
    return this.delete(`company/${downloadUrlPart}`);
  }

  async deletePublicFile(downloadUrlPart: string): APIPromise<FileUploadDTO> {
    return this.delete(`public/${downloadUrlPart}`);
  }

  downloadPublicFileUrl(downloadUrlPart: string): string {
    return this.getFullUrl(`public/download/${downloadUrlPart}`);
  }

  downloadCompanyFileUrl(downloadUrlPart: string): string {
    return this.getFullUrl(`download/${downloadUrlPart}`);
  }

  // ====================== Common ======================
  private async uploadFileCommon(args: FileUploadWithUrlRequest): APIPromise<FileUploadDTO> {
    const formData = new FormData();

    if (args.id) {
      formData.append("id", args.id);
    }

    if (args.file) {
      formData.append("file", args.file);
    }
    if (args.fileName) {
      formData.append("fileName", args.fileName);
    }
    if (args.tags) {
      args.tags.forEach(tag => formData.append("tags", tag));
    }
    if (args.desc) {
      formData.append("description", args.desc);
    }

    const headers: any = {
      "Content-Type": "multipart/form-data",
      // Add file size
    };

    if (args.file) {
      headers["Content-Disposition"] = `attachment; filename=${args.file.name}; size=${args.file.size};`;
    }

    const cb = (progressEvent: AxiosProgressEvent) => {
      if (args.progressCallback) {
        let percent = (progressEvent.loaded * 100) / progressEvent.total!!;
        // round to 2 decimal places
        percent = Math.round(percent * 100) / 100;
        args.progressCallback(percent);
      }
    };

    return this.post(args.urlPath, formData, { headers: headers, onUploadProgress: cb });
  }
}

export const FilesApi = new FilesApiDefinition();
